// 牌局線路格式：伺服器（Durable Object）與瀏覽器之間傳遞的狀態與事件
// 對應 docs/Server-Table-Design.md
//
// - 狀態序列化：TRoundState 的 Set／Map 轉成陣列，才能 JSON 傳輸
// - 遮蔽：只留下指定座位（本家）看得到的資訊；他家暗牌、牌山、他家可吃碰胡的選項一律蓋掉
// - 牌編號打亂：牌的 id 原本依花色順序產生（id 就能反推是哪張牌），
//   出站前把每張牌換成本局專屬的隨機編號；入站動作再換回來
//
// 本檔是純函式，伺服器與瀏覽器共用，不可依賴 Phaser 或 DOM。

import { PlayerActionType, type TPlayerAction } from '@core/actions';
import type { TRoundEventMap } from '@core/events';
import type { THand } from '@core/hand';
import { MeldType, type TMeld } from '@core/meld';
import { RoundPhase, type TRoundState } from '@core/round';
import type { TDiscardReaction } from '@core/ruleset';
import { isFlower, type TTile } from '@core/tile';
import { Suit, type TSeat } from '@core/types';
import type { TWall } from '@core/wall';
import type { IRoundSettlement } from './account-service';

const TILE_COUNT = 144;
const HIDDEN_SUIT = Suit.Man; // 蓋住的牌一律顯示成一萬（畫面只會畫牌背）
const HIDDEN_RANK = 1;

export type TRoundEventName = keyof TRoundEventMap;

/** TRoundState 的 JSON 版本：Set／Map 改成陣列。 */
export interface IWireState {
  readonly wall: TWall;
  readonly hands: TRoundState['hands'];
  readonly discards: TRoundState['discards'];
  readonly config: TRoundState['config'];
  readonly currentSeat: TSeat;
  readonly phase: TRoundState['phase'];
  readonly declaredTenpai: readonly TSeat[];
  readonly lastDrawnTile: TTile | null;
  readonly lastDrawnSeat: TSeat | null;
  readonly lastDrawWasKong: boolean;
  readonly pendingDiscard: TRoundState['pendingDiscard'];
  readonly pendingAddedKong: TRoundState['pendingAddedKong'];
  readonly pendingReactionSeats: readonly TSeat[];
  readonly pendingReactions: readonly (readonly [
    TSeat,
    { readonly reactions: readonly TDiscardReaction[]; readonly canHu: boolean },
  ])[];
  readonly receivedReactions: readonly (readonly [TSeat, TPlayerAction])[];
  readonly pendingHuSeats: readonly TSeat[];
  readonly pendingHuIndex: number;
  readonly pendingMeldSeats: readonly TSeat[];
  readonly pendingMeldIndex: number;
  readonly initialKongChecksCompleted: number;
}

/** 一個事件與它發生當下的（已遮蔽）狀態；畫面先套狀態再派發事件，與本機 CRound 時序一致。 */
export interface IWireEvent {
  readonly name: TRoundEventName;
  readonly payload: unknown;
  readonly state: IWireState;
}

/** 一次伺服器操作的結果：依序發生的事件，以及操作結束後的狀態。 */
export interface IWireBatch {
  readonly events: readonly IWireEvent[];
  readonly state: IWireState;
  /** 這一手在這次操作中結束時，伺服器的結算結果（Worker 附加）。 */
  readonly settlement?: IRoundSettlement | null;
}

export const toWireState = (state: Readonly<TRoundState>): IWireState => ({
  wall: state.wall,
  hands: state.hands,
  discards: state.discards,
  config: state.config,
  currentSeat: state.currentSeat,
  phase: state.phase,
  declaredTenpai: [...state.declaredTenpai],
  lastDrawnTile: state.lastDrawnTile,
  lastDrawnSeat: state.lastDrawnSeat,
  lastDrawWasKong: state.lastDrawWasKong,
  pendingDiscard: state.pendingDiscard,
  pendingAddedKong: state.pendingAddedKong,
  pendingReactionSeats: [...state.pendingReactionSeats],
  pendingReactions: [...state.pendingReactions.entries()],
  receivedReactions: [...state.receivedReactions.entries()],
  pendingHuSeats: [...state.pendingHuSeats],
  pendingHuIndex: state.pendingHuIndex,
  pendingMeldSeats: [...state.pendingMeldSeats],
  pendingMeldIndex: state.pendingMeldIndex,
  initialKongChecksCompleted: state.initialKongChecksCompleted,
});

export const fromWireState = (wire: IWireState): TRoundState => ({
  wall: wire.wall,
  hands: [...wire.hands] as TRoundState['hands'],
  discards: wire.discards.map((river) => [...river]) as TRoundState['discards'],
  config: wire.config,
  currentSeat: wire.currentSeat,
  phase: wire.phase,
  declaredTenpai: new Set(wire.declaredTenpai),
  lastDrawnTile: wire.lastDrawnTile,
  lastDrawnSeat: wire.lastDrawnSeat ?? null,
  lastDrawWasKong: wire.lastDrawWasKong,
  pendingDiscard: wire.pendingDiscard,
  pendingAddedKong: wire.pendingAddedKong,
  pendingReactionSeats: new Set(wire.pendingReactionSeats),
  pendingReactions: new Map(wire.pendingReactions),
  receivedReactions: new Map(wire.receivedReactions),
  pendingHuSeats: [...wire.pendingHuSeats],
  pendingHuIndex: wire.pendingHuIndex,
  pendingMeldSeats: [...wire.pendingMeldSeats],
  pendingMeldIndex: wire.pendingMeldIndex,
  initialKongChecksCompleted: wire.initialKongChecksCompleted,
});

// ---- 遮蔽 ----

const hide = (tile: TTile): TTile => ({ id: tile.id, suit: HIDDEN_SUIT, rank: HIDDEN_RANK });

/** 蓋住一張牌（只留 id）；伺服器遮蔽其他回傳資料時共用。 */
export const hideTile = hide;

/** 他家手牌：花牌公開（開局補花前就看得到），其餘暗牌與暗槓蓋住。 */
const maskHand = (hand: THand): THand => ({
  concealed: hand.concealed.map((tile) => (isFlower(tile) ? tile : hide(tile))),
  melds: hand.melds.map((meld): TMeld =>
    meld.type === MeldType.KongConcealed ? { ...meld, tiles: meld.tiles.map(hide) } : meld
  ),
  flowers: hand.flowers,
});

const maskWall = (wall: TWall): TWall => ({
  ...wall,
  tiles: wall.tiles.map(hide),
  sides: wall.sides.map((side) => side.map(([a, b]) => [hide(a), hide(b)] as const)),
});

/** 只留 viewer 看得到的資訊。牌局結束後攤牌，整份公開。 */
export const maskWireState = (wire: IWireState, viewer: TSeat, revealAll = false): IWireState => {
  if (revealAll) return wire;
  const onlyViewer = (seats: readonly TSeat[]): TSeat[] => seats.filter((s) => s === viewer);
  return {
    ...wire,
    wall: maskWall(wire.wall),
    hands: wire.hands.map((hand, seat) =>
      seat === viewer ? hand : maskHand(hand)
    ) as unknown as IWireState['hands'],
    // 依「誰摸的」判斷：輪到本家時，上一家摸進還沒打出的牌仍要蓋住
    lastDrawnTile:
      wire.lastDrawnTile && wire.lastDrawnSeat !== viewer
        ? hide(wire.lastDrawnTile)
        : wire.lastDrawnTile,
    // 誰正被詢問吃碰胡會洩漏他家手牌；只留本家自己的
    pendingReactionSeats: onlyViewer(wire.pendingReactionSeats),
    pendingReactions: wire.pendingReactions.filter(([seat]) => seat === viewer),
    receivedReactions: wire.receivedReactions.map(([seat, action]) =>
      seat === viewer ? [seat, action] : [seat, { type: PlayerActionType.Pass }]
    ),
    pendingHuSeats: onlyViewer(wire.pendingHuSeats),
    pendingHuIndex: 0,
    pendingMeldSeats: onlyViewer(wire.pendingMeldSeats),
    pendingMeldIndex: 0,
  };
};

/** 事件遮蔽；回傳 null 表示這個事件不送給 viewer（例如他家的吃碰胡詢問）。 */
export const maskWireEvent = <K extends TRoundEventName>(
  name: K,
  payload: TRoundEventMap[K],
  viewer: TSeat,
  phase: TRoundState['phase'] // 事件發生當下的階段
): TRoundEventMap[K] | null => {
  switch (name) {
    case 'wall.dealt': {
      const p = payload as TRoundEventMap['wall.dealt'];
      return {
        ...p,
        hands: p.hands.map((hand, seat) => (seat === viewer ? hand : maskHand(hand))),
      } as unknown as TRoundEventMap[K];
    }
    case 'tile.drawn': {
      const p = payload as TRoundEventMap['tile.drawn'];
      return (p.seat === viewer ? p : { ...p, tile: hide(p.tile) }) as unknown as TRoundEventMap[K];
    }
    case 'action.available': {
      const p = payload as TRoundEventMap['action.available'];
      if (p.seat === viewer) return payload;
      // 他家輪到出牌是公開的（畫面要排 AI），但能不能自摸、槓不公開；
      // 他家的吃碰胡詢問、起手暗槓詢問（只發給手上有四張的座位）整個不送
      return phase === RoundPhase.WaitingReaction || phase === RoundPhase.InitialKong
        ? null
        : ({ seat: p.seat, reactions: [], canHu: false } as unknown as TRoundEventMap[K]);
    }
    case 'tenpai.changed': {
      const p = payload as TRoundEventMap['tenpai.changed'];
      return p.seat === viewer ? payload : null;
    }
    case 'player.action': {
      const p = payload as TRoundEventMap['player.action'];
      if (p.seat === viewer) return payload;
      // 他家暗槓與「過」不公開細節；其餘（出牌、吃碰明槓、胡）本來就攤在桌上
      if (
        p.action.type === PlayerActionType.KongConcealed ||
        p.action.type === PlayerActionType.Pass
      )
        return null;
      return payload;
    }
    case 'meld.formed': {
      const p = payload as TRoundEventMap['meld.formed'];
      return p.seat !== viewer && p.meld.type === MeldType.KongConcealed
        ? ({
            ...p,
            meld: { ...p.meld, tiles: p.meld.tiles.map(hide) },
          } as unknown as TRoundEventMap[K])
        : payload;
    }
    default:
      return payload;
  }
};

// ---- 牌編號打亂 ----

export interface ITileIdScramble {
  readonly toPublic: readonly number[]; // 真實 id → 對外 id
  readonly toReal: readonly number[]; // 對外 id → 真實 id
}

/** 以伺服器亂數產生本局的編號對照（Fisher-Yates）；對外 id 落在 0..143，與真實 id 無關。 */
export const createTileIdScramble = (rng: () => number): ITileIdScramble => {
  const toPublic = Array.from({ length: TILE_COUNT }, (_, i) => i);
  for (let i = TILE_COUNT - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [toPublic[i], toPublic[j]] = [toPublic[j]!, toPublic[i]!];
  }
  const toReal = Array<number>(TILE_COUNT);
  toPublic.forEach((pub, real) => (toReal[pub] = real));
  return { toPublic, toReal };
};

const isTileLike = (value: object): value is TTile =>
  'id' in value &&
  'suit' in value &&
  'rank' in value &&
  typeof (value as TTile).id === 'number' &&
  typeof (value as TTile).suit === 'string';

/** 深層走訪 JSON 值，把每張牌的 id 換成 map(id)；其他內容原樣複製。 */
export const mapTileIds = <T>(value: T, map: readonly number[]): T => {
  const visit = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(visit);
    if (v === null || typeof v !== 'object') return v;
    if (isTileLike(v)) return { ...v, id: map[v.id] ?? v.id };
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(v)) out[key] = visit(inner);
    return out;
  };
  return visit(value) as T;
};
